/**
 * La clasificación en el proyector.
 *
 * **Los empates comparten posición** (§7.5), y eso se ve: dos primeros llevan los dos el mismo
 * número. El orden de dibujado los separa por tiempo, pero la posición mostrada es la misma, y esa
 * distinción es justo la que evita la discusión de aula.
 *
 * La reordenación se anima, salvo que el sistema pida movimiento reducido: entonces cambia de
 * golpe y no se pierde nada, porque la posición está escrita al lado de cada nombre.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HostLeaderboardRow, HostLeaderboardView } from '@clica/shared';

export function Clasificacion({
  leaderboard,
  expandible = true,
}: {
  leaderboard: HostLeaderboardView;
  expandible?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [todo, setTodo] = useState(false);

  const filas = todo ? leaderboard.rows : leaderboard.rows.slice(0, 5);
  const hayMas = leaderboard.total > filas.length;

  return (
    <section aria-labelledby="clasificacion-titulo" className="mt-8">
      <h2 id="clasificacion-titulo" className="font-display text-3xl font-bold">
        {t('game.rank.title')}
      </h2>

      <ol className="mt-4 flex flex-col gap-2">
        {filas.map((row, index) => (
          <FilaClasificacion
            key={row.participantId}
            row={row}
            empatado={comparteRango(filas, index)}
          />
        ))}
      </ol>

      {expandible && (hayMas || todo) ? (
        <button
          type="button"
          onClick={() => setTodo((previo) => !previo)}
          className="border-line mt-4 rounded-lg border px-4 py-2 text-sm"
        >
          {todo ? t('game.rank.showTop') : t('game.rank.showAll')}
        </button>
      ) : null}
    </section>
  );
}

/** Si comparte posición con alguien vecino, para poder decirlo con palabras y no solo por el número. */
function comparteRango(filas: readonly HostLeaderboardRow[], index: number): boolean {
  const actual = filas[index]?.rank;
  return filas.some((fila, i) => i !== index && fila.rank === actual);
}

function FilaClasificacion({
  row,
  empatado,
}: {
  row: HostLeaderboardRow;
  empatado: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    // Envuelve: a 320 px el puesto, el nombre, «Empatados» y los puntos no caben en una fila, y
    // sin envolver eso no era un apretón sino desbordamiento de la página entera.
    <li className="bg-surface border-line flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border-2 px-4 py-3 text-2xl">
      <span className="min-w-16 shrink-0 text-right font-bold tabular-nums">
        {t('game.rank.position', { rank: row.rank })}
      </span>
      <span className="min-w-0 flex-1 truncate">
        {row.label}
        {row.label === row.displayName ? null : (
          /*
           * El profesor ve siempre el nombre real, aunque la clase vea el alias (§7.2).
           *
           * El `/70` está medido: 5,49:1 sobre el fondo de la fila. Con `/60` daba 4,39:1 y la
           * auditoría lo cazó, por debajo del 4,5:1 que exige AA para texto normal.
           */
          <span className="text-text-dim ml-3 text-base">{row.displayName}</span>
        )}
      </span>
      {empatado ? (
        <span className="text-text-dim shrink-0 text-sm">{t('game.rank.tied')}</span>
      ) : null}
      <span className="ml-auto tabular-nums">{t('game.rank.points', { points: row.points })}</span>
    </li>
  );
}

/**
 * El podio.
 *
 * **Con menos de tres participantes se muestran los que hay**, sin huecos vacíos ni puestos
 * inventados: un podio de tres con dos personas y un cajón vacío es peor que un podio de dos.
 *
 * Los tres puestos se distinguen por altura, número y palabra: en escala de grises se siguen
 * distinguiendo (CE-010).
 */
export function Podio({ rows }: { rows: readonly HostLeaderboardRow[] }): React.JSX.Element {
  const { t } = useTranslation();

  const ALTURAS = ['h-48', 'h-36', 'h-28'];
  const NOMBRES = ['game.podium.first', 'game.podium.second', 'game.podium.third'] as const;

  return (
    <section aria-labelledby="podio-titulo" className="mt-10">
      <h2 id="podio-titulo" className="font-display text-center text-4xl font-bold">
        {t('game.podium.title')}
      </h2>

      {/* `flex-wrap` y ancho que cede: tres columnas de 192 px no caben por debajo de 412 px. */}
      <ol className="mt-8 flex flex-wrap items-end justify-center gap-4">
        {rows.map((row, index) => (
          <li
            key={row.participantId}
            className="flex w-full max-w-48 min-w-0 flex-col items-center gap-2 sm:w-48"
          >
            <span className="truncate text-xl font-semibold">{row.label}</span>
            <span className="text-text-dim text-sm tabular-nums">
              {t('game.rank.points', { points: row.points })}
            </span>
            {/*
             * **Lo que crece es el FONDO, no la caja con el texto dentro.**
             *
             * Con la animación puesta en el mismo elemento que lleva el número del puesto, durante
             * los 420 ms el número aparecía aplastado y estirándose, el filete se reducía a una
             * fracción de píxel, y con el escalonado y el relleno `backwards` el primer puesto
             * pasaba 740 ms sin verse. Proyectado delante de la clase.
             *
             * El fondo es un hermano en posición absoluta que sí escala, y el texto va encima sin
             * ninguna transformación. Sigue siendo solo `transform`, que es lo único que sube al
             * compositor.
             */}
            <div
              className={`relative flex w-full items-center justify-center ${ALTURAS[index] ?? 'h-24'}`}
            >
              <span
                aria-hidden="true"
                /* Filete de tinta: es un relleno hi-vis y sobre papel da 1,16:1. Sin borde, los
                   tres cajones serían tres manchas sin límite de componente. */
                style={
                  {
                    '--retraso': `${String((2 - index) * 160)}ms`,
                    transformOrigin: 'bottom',
                  } as React.CSSProperties
                }
                className="crece bg-hivis border-edge absolute inset-0 rounded-t-xl border-2 border-b-0"
              />
              <span className="text-on-hivis relative flex flex-col items-center">
                <span className="font-display text-h2 font-extrabold tabular-nums">
                  {t('game.rank.position', { rank: row.rank })}
                </span>
                <span className="text-micro font-display font-bold">
                  {t(NOMBRES[index] ?? 'game.podium.third')}
                </span>
              </span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
